const findWithin = (root, selector) => {
  const matches = root instanceof Element && root.matches(selector) ? [root] : [];
  return [...matches, ...root.querySelectorAll(selector)];
};

const initialiseFinder = (finder) => {
  if (finder.dataset.faunivaInitialised === 'true') return;
  finder.dataset.faunivaInitialised = 'true';

  const options = [...finder.querySelectorAll('[data-finder-option]')];
  const resultCopy = finder.querySelector('[data-finder-copy]');
  const resultLink = finder.querySelector('[data-finder-link]');
  const defaultLabel = resultLink?.dataset.defaultLabel || resultLink?.textContent.trim() || '';
  const defaultUrl = resultLink?.dataset.defaultUrl || resultLink?.getAttribute('href') || '';
  const unavailableLabel = resultLink?.dataset.unavailableLabel || '';

  const selectOption = (option) => {
    options.forEach((item) => {
      const active = item === option;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });

    if (resultCopy) resultCopy.textContent = option.dataset.resultCopy || '';

    if (resultLink) {
      const resultUrl = option.dataset.resultUrl || defaultUrl;
      resultLink.textContent = resultUrl ? (option.dataset.resultLabel || defaultLabel) : (option.dataset.unavailableLabel || unavailableLabel || defaultLabel);

      if (resultUrl) {
        resultLink.href = resultUrl;
        resultLink.removeAttribute('aria-disabled');
      } else {
        resultLink.removeAttribute('href');
        resultLink.setAttribute('aria-disabled', 'true');
      }
    }
  };

  options.forEach((option) => option.addEventListener('click', () => selectOption(option)));

  const activeOption = options.find((option) => option.classList.contains('is-active')) || options[0];
  if (activeOption) selectOption(activeOption);
};

const initialiseLocaleForm = (form) => {
  if (form.dataset.faunivaInitialised === 'true') return;
  form.dataset.faunivaInitialised = 'true';

  form.querySelectorAll('select').forEach((select) => {
    select.addEventListener('change', () => form.submit());
  });
};

const initialisePrivacyPreferences = (link) => {
  if (link.dataset.faunivaInitialised === 'true') return;
  link.dataset.faunivaInitialised = 'true';

  link.addEventListener('click', (event) => {
    const privacyBanner = window.privacyBanner || window.Shopify?.privacyBanner;

    if (typeof privacyBanner?.showPreferences === 'function') {
      event.preventDefault();
      privacyBanner.showPreferences();
      return;
    }

    const themePrivacyBar = document.querySelector('privacy-bar');
    if (typeof themePrivacyBar?.show === 'function') {
      event.preventDefault();
      themePrivacyBar.show();
    }
  });
};

const openFaunivaVideos = new Set();
const syncVideoScrollLock = () => document.documentElement.classList.toggle('fauniva-video-open', openFaunivaVideos.size > 0);

const initialiseVideoTrigger = (trigger) => {
  if (trigger.dataset.faunivaInitialised === 'true') return;
  const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
  if (!dialog || typeof dialog.showModal !== 'function') return;
  trigger.dataset.faunivaInitialised = 'true';
  trigger.disabled = false;

  let video = dialog.querySelector('video');
  let savedScroll = { x: 0, y: 0 };
  const errorStatus = dialog.querySelector('[data-fauniva-video-status]');
  const closeButton = dialog.querySelector('[data-fauniva-video-close]');
  const stopVideo = () => {
    if (!video) return;
    video.pause();
    try { video.currentTime = 0; } catch (_) { /* Not yet seekable. */ }
  };
  const closeDialog = () => {
    stopVideo();
    openFaunivaVideos.delete(dialog);
    syncVideoScrollLock();
    if (dialog.open) dialog.close();
  };

  trigger.addEventListener('click', () => {
    if (dialog.open) return;
    for (const opened of [...openFaunivaVideos]) {
      if (opened !== dialog) opened.close();
    }
    savedScroll = { x: window.scrollX, y: window.scrollY };
    if (!video) {
      const template = dialog.querySelector('[data-fauniva-video-template]');
      const mount = dialog.querySelector('[data-fauniva-video-mount]');
      if (template && mount) mount.append(template.content.cloneNode(true));
      video = dialog.querySelector('video');
      const fitVideo = () => {
        if (video?.videoWidth && video.videoHeight) dialog.style.setProperty('--fauniva-video-ratio', String(video.videoWidth / video.videoHeight));
      };
      video?.addEventListener('loadedmetadata', fitVideo);
      fitVideo();
      video?.addEventListener('error', () => {
        // Source errors do not bubble; HLS may fail while the MP4 fallback succeeds.
        if ((video.error || video.networkState === 3) && errorStatus) errorStatus.hidden = false;
      }, true);
      video?.addEventListener('playing', () => { if (errorStatus) errorStatus.hidden = true; });
    }
    if (errorStatus) errorStatus.hidden = true;
    dialog.showModal();
    openFaunivaVideos.add(dialog);
    syncVideoScrollLock();
    closeButton?.focus({ preventScroll: true });
    if (video && (video.error || video.networkState === 3)) video.load();
    video?.play()?.catch((error) => {
      // Native controls remain available if the browser requires another tap.
      if ((video.error || video.networkState === 3 || error.name === 'NotSupportedError') && errorStatus) errorStatus.hidden = false;
    });
  });

  closeButton?.addEventListener('click', closeDialog);
  dialog.addEventListener('click', (event) => {
    const box = dialog.getBoundingClientRect();
    if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) closeDialog();
  });
  dialog.addEventListener('cancel', () => {
    stopVideo();
    openFaunivaVideos.delete(dialog);
    syncVideoScrollLock();
  });
  dialog.addEventListener('close', () => {
    if (dialog.open) return; // Ignore a queued close event after a rapid reopen.
    stopVideo();
    openFaunivaVideos.delete(dialog);
    syncVideoScrollLock();
    if (trigger.isConnected && openFaunivaVideos.size === 0) {
      trigger.focus({ preventScroll: true });
      window.scrollTo({ left: savedScroll.x, top: savedScroll.y, behavior: 'instant' });
    }
  });
};

const initialiseDrawerTransfer = (trigger) => {
  if (trigger.dataset.faunivaInitialised === 'true') return;
  trigger.dataset.faunivaInitialised = 'true';

  trigger.addEventListener('click', async (event) => {
    const targetDrawer = document.getElementById(trigger.dataset.faunivaDrawerTransfer);
    const sourceDrawer = trigger.closest('navigation-drawer');

    if (!targetDrawer || typeof targetDrawer.show !== 'function') return;

    event.preventDefault();
    event.stopPropagation();

    if (sourceDrawer?.open && typeof sourceDrawer.hide === 'function') {
      await sourceDrawer.hide();
    }

    targetDrawer.show();
  });
};

const faunivaSceneInstances = new WeakMap();

const initialiseFaunivaScene = (scene) => {
  if (faunivaSceneInstances.has(scene)) return;
  const media = scene.querySelector('.fauniva-habitat__media');
  const image = media?.querySelector('img');
  const row = scene.querySelector('.fauniva-habitat__products');
  if (!media || !image || !row) return;
  const lifecycle = new AbortController();
  const hotspots = [...media.querySelectorAll('[data-scene-x]')];
  const selectors = [...media.querySelectorAll('[data-fauniva-scene-select]')];
  const cards = [...row.querySelectorAll('[data-fauniva-scene-card]')];
  const positionHotspots = () => {
    if (!image.naturalWidth || !image.naturalHeight || !media.clientWidth || !media.clientHeight) return;
    // Coordinates refer to the original photograph, so they stay on the same object after a responsive crop.
    const scale = Math.max(media.clientWidth / image.naturalWidth, media.clientHeight / image.naturalHeight);
    const renderedWidth = image.naturalWidth * scale;
    const renderedHeight = image.naturalHeight * scale;
    const cropLeft = (renderedWidth - media.clientWidth) / 2;
    const cropTop = (renderedHeight - media.clientHeight) / 2;
    hotspots.forEach((hotspot) => {
      const x = Number(hotspot.dataset.sceneX) / 100 * renderedWidth - cropLeft;
      const y = Number(hotspot.dataset.sceneY) / 100 * renderedHeight - cropTop;
      hotspot.style.left = `${Math.max(22, Math.min(media.clientWidth - 22, x))}px`;
      hotspot.style.top = `${Math.max(22, Math.min(media.clientHeight - 22, y))}px`;
    });
    row.querySelectorAll('.fauniva-scene-product__image').forEach((thumbnail) => {
      if (thumbnail.querySelector('img')) return;
      const style = getComputedStyle(thumbnail);
      const x = parseFloat(style.getPropertyValue('--scene-thumb-x')) / 100;
      const y = parseFloat(style.getPropertyValue('--scene-thumb-y')) / 100;
      const height = thumbnail.clientHeight * 4;
      const width = height * image.naturalWidth / image.naturalHeight;
      thumbnail.style.backgroundSize = 'auto 400%';
      thumbnail.style.backgroundPosition = `${thumbnail.clientWidth / 2 - x * width}px ${thumbnail.clientHeight / 2 - y * height}px`;
    });
  };
  const select = (key, reveal = false) => {
    selectors.forEach((hotspot) => {
      const active = hotspot.dataset.faunivaSceneSelect === key;
      hotspot.classList.toggle('is-active', active);
      hotspot.setAttribute('aria-pressed', String(active));
    });
    cards.forEach((card) => {
      const active = card.dataset.faunivaSceneCard === key;
      card.classList.toggle('is-active', active);
      if (active && reveal && row.scrollWidth > row.clientWidth + 1) {
        const cardBox = card.getBoundingClientRect();
        const rowBox = row.getBoundingClientRect();
        const distance = cardBox.left < rowBox.left ? cardBox.left - rowBox.left : cardBox.right > rowBox.right ? cardBox.right - rowBox.right : 0;
        row.scrollBy({left: distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }
    });
  };
  const keyedTarget = (event) => event.target.closest('[data-fauniva-scene-select],[data-fauniva-scene-card]');
  scene.addEventListener('pointerover', (event) => {
    if (event.pointerType === 'touch') return;
    const target = keyedTarget(event);
    if (target && scene.contains(target)) select(target.dataset.faunivaSceneSelect || target.dataset.faunivaSceneCard);
  }, {signal: lifecycle.signal});
  scene.addEventListener('focusin', (event) => {
    const target = keyedTarget(event);
    if (target) select(target.dataset.faunivaSceneSelect || target.dataset.faunivaSceneCard, true);
  }, {signal: lifecycle.signal});
  scene.addEventListener('click', (event) => {
    const target = event.target.closest('[data-fauniva-scene-select]');
    if (target) select(target.dataset.faunivaSceneSelect, true);
  }, {signal: lifecycle.signal});
  scene.addEventListener('keydown', (event) => {
    const target = event.target.closest('[data-fauniva-scene-select]');
    const index = selectors.indexOf(target);
    if (index < 0 || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? selectors.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + selectors.length) % selectors.length;
    selectors[next].focus({preventScroll: true});
    select(selectors[next].dataset.faunivaSceneSelect, true);
  }, {signal: lifecycle.signal});
  image.addEventListener('load', positionHotspots, {signal: lifecycle.signal});
  let observer;
  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(positionHotspots);
    observer.observe(media);
  } else window.addEventListener('resize', positionHotspots, {signal: lifecycle.signal});
  positionHotspots();
  if (selectors.length) select(selectors[0].dataset.faunivaSceneSelect);
  faunivaSceneInstances.set(scene, () => {lifecycle.abort(); observer?.disconnect(); faunivaSceneInstances.delete(scene);});
};

const initialiseCompactFaq = (faq) => {
  if (faq.dataset.faunivaAccordionInitialised === 'true') return;
  faq.dataset.faunivaAccordionInitialised = 'true';
  faq.addEventListener('toggle', (event) => {
    const opened = event.target;
    if (opened.tagName !== 'DETAILS' || !opened.open) return;
    faq.querySelectorAll('details[open]').forEach((item) => {if (item !== opened) item.open = false;});
  }, true);
};

const initialiseFauniva = (root = document) => {
  findWithin(root, "[data-fauniva-scene]").forEach(initialiseFaunivaScene);
  findWithin(root, "#fauniva-home-faq").forEach(initialiseCompactFaq);
  findWithin(root, '[data-fauniva-finder]').forEach(initialiseFinder);
  findWithin(root, 'form.fauniva-footer__locale').forEach(initialiseLocaleForm);
  findWithin(root, '[data-fauniva-privacy-preferences]').forEach(initialisePrivacyPreferences);
  findWithin(root, '[data-fauniva-video-trigger]').forEach(initialiseVideoTrigger);
  findWithin(root, '[data-fauniva-drawer-transfer]').forEach(initialiseDrawerTransfer);
  findWithin(root, '.fauniva-nav-hub').forEach((hub) => {
    if (hub.dataset.faunivaAnchorsInitialised === 'true') return;
    hub.dataset.faunivaAnchorsInitialised = 'true';
    hub.addEventListener('click', async (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const destination = new URL(link.href);
      if (destination.origin !== location.origin || destination.pathname !== location.pathname || destination.search !== location.search || !destination.hash) return;
      const target = document.getElementById(decodeURIComponent(destination.hash.slice(1)));
      const drawer = hub.closest('navigation-drawer');
      if (!target || typeof drawer?.hide !== 'function') return;
      event.preventDefault();
      await drawer.hide();
      location.hash = destination.hash;
      target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initialiseFauniva(document), { once: true });
} else {
  initialiseFauniva(document);
}

document.addEventListener('shopify:section:load', (event) => initialiseFauniva(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  findWithin(event.target, '[data-fauniva-scene]').forEach((scene) => faunivaSceneInstances.get(scene)?.());
  findWithin(event.target, '[data-fauniva-video-dialog]').forEach((dialog) => {
    dialog.querySelector('video')?.pause();
    if (dialog.open) dialog.close();
    openFaunivaVideos.delete(dialog);
  });
  syncVideoScrollLock();
});
document.addEventListener('visibilitychange', () => {
  if (document.hidden) openFaunivaVideos.forEach((dialog) => dialog.querySelector('video')?.pause());
});
window.addEventListener('pagehide', () => {
  [...openFaunivaVideos].forEach((dialog) => {
    dialog.querySelector('video')?.pause();
    if (dialog.open) dialog.close();
    openFaunivaVideos.delete(dialog);
  });
  syncVideoScrollLock();
});
